
import Vue from 'vue'
import { apiService } from '@/service/api.service'
import AdminTableSubheader from '../shared/AdminTableSubheader.vue'

const EMPTY_FORM = () => ({
  name: '',
  order_no: '',
  licence_type_id: null as number | null,
  duration: 365,
  seats: 1,
  email: ''
})

export default Vue.extend({
  name: 'OrderNumbers',
  components: { AdminTableSubheader },
  data () {
    return {
      loading: false,
      saving: false,
      loadingTypes: false,
      orders: [] as any[],
      licenceTypes: [] as Array<{ id: number; label: string }>,
      page: 1,
      itemsPerPage: 10,
      totalItems: 0,
      search: '',
      searchTimer: null as ReturnType<typeof setTimeout> | null,
      dialog: false,
      formValid: false,
      editingId: null as number | null,
      form: EMPTY_FORM(),
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      headers: [
        { text: 'Status', value: 'status', sortable: false },
        { text: 'Customer', value: 'name' },
        { text: 'Order No', value: 'order_no' },
        { text: 'Licence Type', value: 'licence_type_label' },
        { text: 'Duration', value: 'duration' },
        { text: 'Seats', value: 'seats' },
        { text: 'Email', value: 'email', sortable: false },
        { text: 'Used At', value: 'used_at' },
        { text: 'Created At', value: 'created_at' },
        { text: 'Actions', value: 'actions', sortable: false }
      ]
    }
  },
  mounted () {
    this.loadLicenceTypes()
    this.loadOrders()
  },
  methods: {
    async loadOrders () {
      this.loading = true
      try {
        const { data } = await apiService.get('/licences/order-numbers', {
          params: { page: this.page, per_page: this.itemsPerPage, q: this.search }
        })
        if (data.ok) {
          this.orders = data.orders
          this.totalItems = data.page_info.total_items
        }
      } catch (e) {
        this.showSnackbar('Failed to load orders', 'error')
      } finally {
        this.loading = false
      }
    },

    async loadLicenceTypes () {
      this.loadingTypes = true
      try {
        const { data } = await apiService.get('/get_all_licences_info')
        this.licenceTypes = data.licences || []
      } catch (e) {
        console.error('Failed to load licence types', e)
      } finally {
        this.loadingTypes = false
      }
    },

    onSearch () {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.page = 1
        this.loadOrders()
      }, 400)
    },

    updateItemsPerPage (val: number) {
      this.itemsPerPage = val
      this.page = 1
      this.loadOrders()
    },

    openCreateDialog () {
      this.editingId = null
      this.form = EMPTY_FORM()
      this.dialog = true
      this.$nextTick(() => {
        const f = this.$refs.orderForm as any
        if (f) f.resetValidation()
      })
    },

    openEditDialog (item: any) {
      this.editingId = item.id
      this.form = {
        name: item.name,
        order_no: item.order_no,
        licence_type_id: item.licence_type_id,
        duration: item.duration,
        seats: item.seats,
        email: item.email || ''
      }
      this.dialog = true
    },

    closeDialog () {
      this.dialog = false
      this.editingId = null
      this.form = EMPTY_FORM()
    },

    async saveOrder () {
      const f = this.$refs.orderForm as any
      if (!f.validate()) return
      this.saving = true
      try {
        const payload = {
          name: this.form.name,
          order_no: this.form.order_no,
          licence_type_id: this.form.licence_type_id,
          duration: this.form.duration,
          seats: this.form.seats,
          email: this.form.email || null
        }
        if (this.editingId) {
          await apiService.put(`/licences/order-numbers/${this.editingId}`, payload)
          this.showSnackbar('Order updated successfully')
        } else {
          await apiService.post('/licences/order-numbers', payload)
          this.showSnackbar('Order created successfully')
        }
        this.closeDialog()
        this.loadOrders()
      } catch (err: any) {
        const msg = err?.response?.data?.message || 'An error occurred'
        this.showSnackbar(msg, 'error')
      } finally {
        this.saving = false
      }
    },

    async deleteOrder (item: any) {
      if (!confirm(`Delete order "${item.order_no}"?`)) return
      try {
        await apiService.delete(`/licences/order-numbers/${item.id}`)
        this.showSnackbar('Order deleted')
        this.loadOrders()
      } catch (err: any) {
        const msg = err?.response?.data?.message || 'Failed to delete'
        this.showSnackbar(msg, 'error')
      }
    },

    formatDate (d: string) {
      return new Date(d).toLocaleString()
    },

    showSnackbar (text: string, color = 'success') {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    }
  }
})
